import { useReducer, useCallback } from "react";
import type { GameState, Direction, Puzzle, Token } from "../types.ts";
import { applyDirection } from "../engine/movement.ts";
import { cloneTokens, tokensEqual } from "../engine/grid.ts";
import { calculateStars } from "../engine/scoring.ts";

type GameAction =
  | { type: "MOVE"; direction: Direction }
  | { type: "UNDO" }
  | { type: "RESET" }
  | { type: "NEW_PUZZLE"; puzzle: Puzzle };

function initGameState(puzzle: Puzzle): GameState {
  return {
    puzzle,
    tokens: cloneTokens(puzzle.initialTokens),
    moveHistory: [],
    phase: "playing",
    startedAt: Date.now(),
    stars: 0,
  };
}

// Token snapshots for undo
let tokenSnapshots: Token[][] = [];

function gameReducer(state: GameState, action: GameAction): GameState {
  switch (action.type) {
    case "MOVE": {
      if (state.phase !== "playing") return state;

      const result = applyDirection(state.puzzle.grid, state.tokens, action.direction);

      // No-op: if nothing moved, don't count as a move
      if (tokensEqual(result.tokens, state.tokens)) return state;

      tokenSnapshots.push(cloneTokens(state.tokens));

      const newHistory: Direction[] = [...state.moveHistory, action.direction];
      const won = result.won;
      const lost = result.anyLost;

      return {
        ...state,
        tokens: result.tokens,
        moveHistory: newHistory,
        phase: won ? "won" : lost ? "lost" : "playing",
        completedAt: won || lost ? Date.now() : undefined,
        stars: won ? calculateStars(newHistory.length, state.puzzle.optimalMoves) : 0,
      };
    }

    case "UNDO": {
      if (state.moveHistory.length === 0 || state.phase !== "playing") return state;

      const prevTokens = tokenSnapshots.pop();
      if (!prevTokens) return state;

      return {
        ...state,
        tokens: prevTokens,
        moveHistory: state.moveHistory.slice(0, -1),
      };
    }

    case "RESET": {
      tokenSnapshots = [];
      return {
        ...state,
        tokens: cloneTokens(state.puzzle.initialTokens),
        moveHistory: [],
        phase: "playing",
        startedAt: Date.now(),
        stars: 0,
      };
    }

    case "NEW_PUZZLE": {
      tokenSnapshots = [];
      return initGameState(action.puzzle);
    }

    default:
      return state;
  }
}

export function useGame(puzzle: Puzzle) {
  const [state, dispatch] = useReducer(gameReducer, puzzle, (p) => {
    tokenSnapshots = [];
    return initGameState(p);
  });

  const move = useCallback(
    (direction: Direction) => dispatch({ type: "MOVE", direction }),
    []
  );
  const undo = useCallback(() => dispatch({ type: "UNDO" }), []);
  const reset = useCallback(() => dispatch({ type: "RESET" }), []);

  return { state, move, undo, reset };
}
